// Marketing — ad landing pages + order-source report (Phase 1 ads spec,
// mdf-project docs/specs/2026-10-13-phase1-lp-cms-tracking-design.md).
//
// Pure helpers shared by src/views/Marketing/* (no Vue, no axios) so the rules
// the editor applies before asking the API are unit-tested: §2.1 page fields,
// §2.2-2.3 section shapes, §2.4 save vs publish validation, §6.3 ad link and
// preview src, §3.7 / §6.5 report query + display.

// ---------------------------------------------------------------------------
// Sections (§2.3) — the order is the type-picker order.
export const SECTION_TYPES = [
	"hero",
	"product_grid",
	"featured_product",
	"promotion",
	"countdown",
	"coupon",
	"rich_text",
	"faq",
	"line_chat",
	"sticky_cta",
];

export const DEFAULT_LINE_URL = "https://line.me/R/ti/p/@fin9";
export const MAX_SECTIONS = 30;

const emptyCta = () => ({ kind: "scroll", target_section_id: "", link: "" });

// Field list per type = what the API stores (unknown fields are dropped server
// side; we drop them here too so the payload is exactly the contract).
const FIELDS = {
	hero: ["image_key", "image_mobile_key", "video_key", "video_poster_key", "title", "subtitle", "cta_label", "cta_action", "text_theme", "overlay"],
	product_grid: ["title", "product_ids", "columns_mobile"],
	featured_product: ["product_id", "title_override", "badge_text", "show_description"],
	promotion: ["title", "promotion_ids"],
	countdown: ["title", "ends_at", "ended_text", "hide_when_ended", "theme"],
	coupon: ["title", "description", "code", "button_label", "image_key"],
	rich_text: ["html"],
	faq: ["title", "items"],
	line_chat: ["label", "url", "note"],
	sticky_cta: ["label", "sublabel", "cta_action"],
};

export function defaultSection(type, id) {
	const base = { id, type, visible: true };
	switch (type) {
		case "hero":
			return { ...base, image_key: "", image_mobile_key: "", video_key: "", video_poster_key: "", title: "", subtitle: "", cta_label: "", cta_action: emptyCta(), text_theme: "light", overlay: 30 };
		case "product_grid":
			return { ...base, title: "", product_ids: [], columns_mobile: 2 };
		case "featured_product":
			return { ...base, product_id: 0, title_override: "", badge_text: "", show_description: true };
		case "promotion":
			return { ...base, title: "", promotion_ids: [] };
		case "countdown":
			return { ...base, title: "", ends_at: "", ended_text: "", hide_when_ended: false, theme: "dark" };
		case "coupon":
			return { ...base, title: "", description: "", code: "", button_label: "", image_key: "" };
		case "rich_text":
			return { ...base, html: "" };
		case "faq":
			return { ...base, title: "", items: [{ q: "", a: "" }] };
		case "line_chat":
			return { ...base, label: "", url: DEFAULT_LINE_URL, note: "" };
		case "sticky_cta":
			return { ...base, label: "", sublabel: "", cta_action: emptyCta() };
		default:
			return base;
	}
}

const ID_CHARS = "abcdefghijklmnopqrstuvwxyz0123456789";
function randomSuffix() {
	let s = "";
	const c = typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues ? window.crypto : null;
	const buf = c ? c.getRandomValues(new Uint8Array(6)) : null;
	for (let i = 0; i < 6; i++) {
		const n = buf ? buf[i] : Math.floor(Math.random() * 256);
		s += ID_CHARS[n % ID_CHARS.length];
	}
	return s;
}

/** `s_` + 6 chars of [a-z0-9], unique among `existing` (§2.2). */
export function newSectionId(existing, rnd = randomSuffix) {
	const taken = new Set(existing || []);
	for (let i = 0; i < 100; i++) {
		const id = "s_" + rnd();
		if (/^s_[a-z0-9]{6}$/.test(id) && !taken.has(id)) return id;
	}
	return "s_" + Date.now().toString(36).slice(-6);
}

// ---------------------------------------------------------------------------
// Page fields (§2.1)
export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const RESERVED_SLUGS = ["preview"];

/** "" | "format" | "reserved" — the parts of check-slug the browser can know. */
export function slugProblem(slug) {
	const s = String(slug == null ? "" : slug);
	if (s.length < 3 || s.length > 60 || !SLUG_RE.test(s)) return "format";
	if (RESERVED_SLUGS.indexOf(s) !== -1) return "reserved";
	return "";
}

const MDF_HOST = /^([a-z0-9-]+\.)*madamefin\.com$/i;

/** Same-site path or https://*.madamefin.com (§2.2 CTAAction.link, §2.1 fallback_url). */
export function isSafeLink(v) {
	const s = String(v == null ? "" : v).trim();
	if (!s) return false;
	// eslint-disable-next-line no-control-regex
	if (/[\u0000-\u001f\u007f\\]/.test(s)) return false;
	if (s.charAt(0) === "/") return s.charAt(1) !== "/";
	try {
		const u = new URL(s);
		return u.protocol === "https:" && MDF_HOST.test(u.hostname) && !u.username && !u.password;
	} catch (e) {
		return false;
	}
}

// ---------------------------------------------------------------------------
// Validation (§2.4). Errors use the server's shape {section_id, field, rule};
// page-level fields have section_id "".
const LIMITS = {
	hero: { title: 80, subtitle: 160, cta_label: 30 },
	featured_product: { title_override: 80, badge_text: 20 },
	countdown: { title: 80, ended_text: 80 },
	coupon: { description: 200, code: 40, button_label: 30 },
	rich_text: { html: 20000 },
	line_chat: { label: 30, note: 120 },
	sticky_cta: { label: 30, sublabel: 40 },
};
const len = (v) => Array.from(String(v == null ? "" : v)).length;
const blank = (v) => String(v == null ? "" : v).trim() === "";

function validateCta(errs, s, publish, ids) {
	const a = s.cta_action || {};
	if (a.kind === "link") {
		if (!blank(a.link) && !isSafeLink(a.link)) errs.push({ section_id: s.id, field: "cta_action.link", rule: "invalid_link" });
		if (publish && blank(a.link)) errs.push({ section_id: s.id, field: "cta_action.link", rule: "required" });
	} else if (publish && a.kind === "scroll") {
		const t = a.target_section_id;
		if (blank(t)) {
			// A hero / sticky without a target just has no CTA; only a label with no target is an error.
			if (!blank(s.cta_label) || s.type === "sticky_cta") errs.push({ section_id: s.id, field: "cta_action.target_section_id", rule: "required" });
		} else if (!ids.has(t)) {
			errs.push({ section_id: s.id, field: "cta_action.target_section_id", rule: "invalid_target" });
		}
	}
}

function draftErrors(page, publish) {
	const errs = [];
	const p = page || {};
	const seo = p.seo || {};
	if (len(p.title) > 200) errs.push({ section_id: "", field: "title", rule: "max_length" });
	if (len(seo.title) > 70) errs.push({ section_id: "", field: "seo.title", rule: "max_length" });
	if (len(seo.description) > 160) errs.push({ section_id: "", field: "seo.description", rule: "max_length" });
	if (p.starts_at && p.ends_at && Date.parse(p.ends_at) <= Date.parse(p.starts_at)) {
		errs.push({ section_id: "", field: "ends_at", rule: "range" });
	}
	if (!blank(p.fallback_url) && !isSafeLink(p.fallback_url)) errs.push({ section_id: "", field: "fallback_url", rule: "invalid_link" });

	const sections = Array.isArray(p.sections) ? p.sections : [];
	if (sections.length > MAX_SECTIONS) errs.push({ section_id: "", field: "sections", rule: "too_many" });
	const seen = new Set();
	const ids = new Set(sections.map((s) => s && s.id));
	sections.forEach((s) => {
		if (!s) return;
		if (seen.has(s.id)) errs.push({ section_id: s.id, field: "id", rule: "duplicate" });
		seen.add(s.id);
		const lim = LIMITS[s.type] || {};
		Object.keys(lim).forEach((f) => {
			if (len(s[f]) > lim[f]) errs.push({ section_id: s.id, field: f, rule: "max_length" });
		});
		if (s.type === "hero" || s.type === "sticky_cta") validateCta(errs, s, publish, ids);
		if (s.type === "line_chat" && !blank(s.url) && String(s.url).indexOf("https://line.me/") !== 0) {
			errs.push({ section_id: s.id, field: "url", rule: "invalid_link" });
		}
		if (s.type === "product_grid" && (s.product_ids || []).length > 24) errs.push({ section_id: s.id, field: "product_ids", rule: "too_many" });
		if (s.type === "promotion" && (s.promotion_ids || []).length > 6) errs.push({ section_id: s.id, field: "promotion_ids", rule: "too_many" });
		if (s.type === "faq") {
			const items = s.items || [];
			if (items.length > 20) errs.push({ section_id: s.id, field: "items", rule: "too_many" });
			items.forEach((it, i) => {
				if (len(it && it.q) > 200) errs.push({ section_id: s.id, field: `items.${i}.q`, rule: "max_length" });
				if (len(it && it.a) > 1000) errs.push({ section_id: s.id, field: `items.${i}.a`, rule: "max_length" });
			});
		}
	});
	return errs;
}

/** Save draft (PUT): shape, lengths, links, ≤ 30 sections, unique ids. Empty is fine. */
export function validateDraft(page) {
	return draftErrors(page, false);
}

/** Publish: everything in validateDraft plus the §2.4 publish rules. */
export function validatePublish(page, nowMs = Date.now()) {
	const errs = draftErrors(page, true);
	const p = page || {};
	if (blank(p.title)) errs.push({ section_id: "", field: "title", rule: "required" });
	if (p.ends_at && Date.parse(p.ends_at) <= nowMs) errs.push({ section_id: "", field: "ends_at", rule: "in_past" });
	const sections = (Array.isArray(p.sections) ? p.sections : []).filter(Boolean);
	const visible = sections.filter((s) => s.visible !== false);
	if (!visible.length) errs.push({ section_id: "", field: "sections", rule: "required" });

	let heroes = 0;
	let featured = 0;
	let sticky = 0;
	sections.forEach((s) => {
		switch (s.type) {
			case "hero":
				heroes += 1;
				if (heroes > 1) errs.push({ section_id: s.id, field: "type", rule: "too_many" });
				if (blank(s.image_key) && blank(s.image_mobile_key) && blank(s.video_key)) {
					errs.push({ section_id: s.id, field: "image_key", rule: "required" });
				}
				if (!blank(s.video_key) && blank(s.video_poster_key)) errs.push({ section_id: s.id, field: "video_poster_key", rule: "required" });
				if (s.visible !== false && visible[0] && visible[0].id !== s.id) errs.push({ section_id: s.id, field: "type", rule: "hero_first" });
				break;
			case "product_grid":
				if (!(s.product_ids || []).length) errs.push({ section_id: s.id, field: "product_ids", rule: "required" });
				break;
			case "featured_product":
				featured += 1;
				if (featured > 3) errs.push({ section_id: s.id, field: "type", rule: "too_many" });
				if (!(Number(s.product_id) > 0)) errs.push({ section_id: s.id, field: "product_id", rule: "required" });
				break;
			case "promotion":
				if (!(s.promotion_ids || []).length) errs.push({ section_id: s.id, field: "promotion_ids", rule: "required" });
				break;
			case "countdown":
				if (blank(s.ends_at) || !Number.isFinite(Date.parse(s.ends_at))) errs.push({ section_id: s.id, field: "ends_at", rule: "required" });
				break;
			case "coupon":
				if (blank(s.code)) errs.push({ section_id: s.id, field: "code", rule: "required" });
				break;
			case "faq":
				if (!(s.items || []).some((it) => it && !blank(it.q))) errs.push({ section_id: s.id, field: "items", rule: "required" });
				break;
			case "sticky_cta":
				sticky += 1;
				if (sticky > 1) errs.push({ section_id: s.id, field: "type", rule: "too_many" });
				if (blank(s.label)) errs.push({ section_id: s.id, field: "label", rule: "required" });
				break;
			default:
				break;
		}
	});
	return errs;
}

/** {section_id: {field: rule}} — first rule per field wins. */
export function errorsBySection(errors) {
	const out = {};
	(errors || []).forEach((e) => {
		if (!e) return;
		const sid = e.section_id || "";
		out[sid] = out[sid] || {};
		if (!out[sid][e.field]) out[sid][e.field] = e.rule;
	});
	return out;
}

/** Unavailable picks of one section from LPDetail.warnings[] (§3.2). */
export function warningsFor(warnings, sectionId) {
	const out = { products: [], promotions: [] };
	(warnings || []).forEach((w) => {
		if (!w || w.section_id !== sectionId) return;
		if (w.product_id) out.products.push(Number(w.product_id));
		if (w.promotion_id) out.promotions.push(Number(w.promotion_id));
	});
	return out;
}

// ---------------------------------------------------------------------------
// Payloads — *_key only (the API adds *_url on read, §2.2).
export function sectionSavePayload(s) {
	const out = { id: s.id, type: s.type, visible: s.visible !== false };
	(FIELDS[s.type] || []).forEach((f) => {
		if (s[f] === undefined) return;
		let v = s[f];
		if (f === "cta_action") v = { kind: v.kind === "link" ? "link" : "scroll", target_section_id: v.target_section_id || "", link: v.link || "" };
		else if (f === "items") v = (v || []).map((it) => ({ q: (it && it.q) || "", a: (it && it.a) || "" }));
		else if (Array.isArray(v)) v = v.slice();
		if (f === "html") v = sanitizeLpHtml(v);
		out[f] = v;
	});
	return out;
}

export function pageSavePayload(page, expectedUpdatedAt) {
	const seo = page.seo || {};
	return {
		title: page.title || "",
		slug: page.slug || "",
		starts_at: page.starts_at || null,
		ends_at: page.ends_at || null,
		fallback_url: page.fallback_url || "/",
		seo: {
			title: seo.title || "",
			description: seo.description || "",
			og_image_key: seo.og_image_key || "",
			noindex: seo.noindex !== false,
		},
		sections: (page.sections || []).map(sectionSavePayload),
		expected_updated_at: expectedUpdatedAt || "",
	};
}

// ---------------------------------------------------------------------------
// Links (§1.1 step 7, §6.3)
export const AD_URL_PARAMS =
	"utm_source=facebook&utm_medium=paid&utm_campaign={{campaign.name}}&utm_content={{ad.id}}&utm_term={{adset.name}}";

const trimOrigin = (o) => String(o || "").replace(/\/+$/, "");

export function lpUrl(origin, slug) {
	return `${trimOrigin(origin)}/lp/${slug}`;
}

export function adUrl(origin, slug) {
	return `${lpUrl(origin, slug)}?${AD_URL_PARAMS}`;
}

export function previewSrc(origin, slug, token, updatedAt) {
	return `${lpUrl(origin, slug)}?preview=${encodeURIComponent(token || "")}&_=${encodeURIComponent(updatedAt || "")}`;
}

// ---------------------------------------------------------------------------
// API errors
export function apiErrorCode(err) {
	const d = err && err.response && err.response.data;
	return (d && (d.code || (d.error && d.error.code))) || "";
}

export function isSchemaMissing(err) {
	return apiErrorCode(err) === "schema_missing" || (err && err.response && err.response.status === 503);
}

// ---------------------------------------------------------------------------
// Report (§3.7 / §6.5)
export const REPORT_GROUPS = ["source", "source_medium", "campaign", "content", "term", "landing_page"];
export const REPORT_MODELS = ["last", "first"];
export const SOURCE_CHANNELS = ["shop", "member", "app", "guest", "chat"];
const DAY = 86400000;
const BKK = 7 * 3600000;
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);

/** [from, to] = the last 30 days ending today in Asia/Bangkok, inclusive. */
export function defaultReportRange(now = new Date()) {
	const today = Date.parse(isoDay(now.getTime() + BKK));
	return [isoDay(today - 29 * DAY), isoDay(today)];
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** {ok, params} for /summary, /timeseries, /orders, /export.csv — or {ok:false, error}. */
export function buildReportQuery(f) {
	const r = f && f.range;
	if (!Array.isArray(r) || r.length !== 2 || !DATE_RE.test(r[0] || "") || !DATE_RE.test(r[1] || "")) {
		return { ok: false, error: "invalid_range" };
	}
	const days = (Date.parse(r[1]) - Date.parse(r[0])) / DAY + 1;
	if (!(days >= 1 && days <= 366)) return { ok: false, error: "invalid_range" };
	const params = {
		from: r[0],
		to: r[1],
		model: REPORT_MODELS.indexOf(f.model) !== -1 ? f.model : "last",
		group_by: REPORT_GROUPS.indexOf(f.group_by) !== -1 ? f.group_by : "source_medium",
	};
	["source", "medium", "campaign", "content", "lp_slug"].forEach((k) => {
		const v = String(f[k] == null ? "" : f[k]).trim();
		if (v) params[k] = v;
	});
	if (SOURCE_CHANNELS.indexOf(f.source_channel) !== -1) params.source_channel = f.source_channel;
	if (f.page) params.page = Math.max(1, Number(f.page) || 1);
	if (f.limit) params.limit = Math.min(100, Math.max(1, Number(f.limit) || 20));
	return { ok: true, params };
}

export function keyColumns(groupBy) {
	switch (groupBy) {
		case "source":
			return ["source"];
		case "campaign":
			return ["source", "medium", "campaign"];
		case "content":
			return ["source", "medium", "campaign", "content"];
		case "term":
			return ["source", "medium", "campaign", "term"];
		case "landing_page":
			return ["lp_slug"];
		default:
			return ["source", "medium"];
	}
}

/** Empty key component → "(direct)" for source, "(none)" otherwise (§3.7). */
export function keyLabel(key, col) {
	const v = key && key[col];
	if (v !== undefined && v !== null && String(v) !== "") return String(v);
	return col === "source" ? "(direct)" : "(none)";
}

export function reportCards(totals) {
	const t = totals || {};
	const orders = Number(t.orders) || 0;
	const completed = Number(t.orders_completed) || 0;
	return {
		orders,
		revenue_placed: Number(t.revenue_placed) || 0,
		revenue_completed: Number(t.revenue_completed) || 0,
		completion_pct: orders ? Math.round((completed / orders) * 1000) / 10 : 0,
		aov: Number(t.aov_placed) || 0,
		new_members: t.new_members === null || t.new_members === undefined ? null : Number(t.new_members),
	};
}

const PALETTE = ["#5e72e4", "#2dce89", "#fb6340", "#f5365c", "#11cdef", "#8898aa"];

/** Chart.js data for the daily orders chart: top-5 source/medium + "other". */
export function reportChartData(ts, otherLabel) {
	if (!ts || !Array.isArray(ts.dates)) return { labels: [], datasets: [] };
	const datasets = (ts.series || []).map((s, i) => {
		const other = s.key === "__other__" || (s.key && s.key.source === "__other__");
		const label = other ? otherLabel : `${keyLabel(s.key, "source")} / ${keyLabel(s.key, "medium")}`;
		const color = PALETTE[i % PALETTE.length];
		return { label, data: (s.orders || []).slice(), borderColor: color, backgroundColor: "transparent", pointBackgroundColor: color, fill: false };
	});
	return { labels: ts.dates.slice(), datasets };
}

// ---------------------------------------------------------------------------
// Asia/Bangkok wall clock (§0: dates are +07). The pickers work on a naive
// "yyyy-MM-ddTHH:mm:ss" string in Bangkok time, independent of the browser's
// own time zone; the API gets RFC3339 with +07:00.
const pad2 = (n) => String(n).padStart(2, "0");

/** API RFC3339 → "yyyy-MM-ddTHH:mm:ss" Bangkok wall clock ("" for empty / invalid). */
export function isoToBkkLocal(iso) {
	const ms = Date.parse(iso || "");
	if (!Number.isFinite(ms)) return "";
	return new Date(ms + BKK).toISOString().slice(0, 19);
}

/** Bangkok wall clock "yyyy-MM-ddTHH:mm[:ss]" → RFC3339 "+07:00" (null for empty). */
export function bkkLocalToIso(local) {
	const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(local || "");
	if (!m) return null;
	return `${m[1]}T${m[2]}:${m[3]}:${m[4] || "00"}+07:00`;
}

/** "yyyy-MM-dd HH:mm" in Bangkok, "-" for empty. */
export function fmtBkk(iso) {
	const l = isoToBkkLocal(iso);
	return l ? `${l.slice(0, 10)} ${l.slice(11, 16)}` : "-";
}

/** el-tag type for a list item's live_state. */
export function liveStateTag(state) {
	return { live: "success", scheduled: "warning", ended: "info", draft: "", archived: "info" }[state] || "";
}

// ---------------------------------------------------------------------------
// rich_text sanitiser (Verifier 2 #1). vue2-editor (Quill) writes HTML into a
// LIVE element when it loads a value, so a stored `<img src=x onerror=…>`
// would run in the BO session. Everything bound to the editor, saved or
// previewed goes through this allow-list first. DOMParser builds an inert
// document: nothing loads and no handler runs while we parse. The API
// sanitises again server-side, and www sanitises on render.
//
// The allow-list is EXACTLY the API's (mdf-bo-api controllers/
// Landing_page_sanitize.go, bluemonday lpRichTextPolicy): what the editor shows
// is what gets saved and what www renders. Pinned by
// tests/unit/marketingEditorAllowList.spec.js — change both together.
export const LP_HTML_TAGS = ["p", "br", "strong", "em", "u", "s", "ul", "ol", "li", "h2", "h3", "h4", "blockquote", "a", "img"];
export const LP_HTML_ATTRS = { a: ["href", "rel", "target"], img: ["src", "alt"] };
// vue2-editor toolbar + Quill formats limited to what survives the allow-list
// (no H1, alignment, colour, highlight, indent: those vanished after save).
export const LP_EDITOR_TOOLBAR = [
	[{ header: [2, 3, 4, false] }],
	["bold", "italic", "underline", "strike"],
	[{ list: "ordered" }, { list: "bullet" }],
	["blockquote"],
	["link", "image"],
	["clean"],
];
export const LP_EDITOR_FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "blockquote", "link", "image"];

const KEEP_TAGS = new Set(LP_HTML_TAGS.map((t) => t.toUpperCase()));
const DROP_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "FRAME", "FRAMESET", "OBJECT", "EMBED", "SVG", "MATH", "TEMPLATE", "NOSCRIPT",
	"LINK", "META", "BASE", "FORM", "INPUT", "BUTTON", "TEXTAREA", "SELECT", "VIDEO", "AUDIO", "SOURCE", "TITLE", "HEAD"]);
const ATTRS = { A: LP_HTML_ATTRS.a, IMG: LP_HTML_ATTRS.img };

function safeUrl(v, forImg) {
	// eslint-disable-next-line no-control-regex
	const s = String(v || "").replace(/[\u0000- \u007f]/g, "");
	if (!s) return false;
	if (/^(\/(?!\/)|#|\?)/.test(s)) return true;
	const m = /^([a-z][a-z0-9+.-]*):/i.exec(s);
	if (!m) return !/^\/\//.test(s); // relative path ("img/a.jpg") ok, protocol-relative not
	const scheme = m[1].toLowerCase();
	return forImg ? scheme === "https" || scheme === "http" : ["https", "http", "mailto", "tel"].indexOf(scheme) !== -1;
}

function cleanNode(node) {
	Array.from(node.childNodes).forEach((child) => {
		if (child.nodeType === 3) return; // text
		if (child.nodeType !== 1) {
			child.remove(); // comments, processing instructions
			return;
		}
		const tag = child.tagName.toUpperCase();
		if (DROP_TAGS.has(tag)) {
			child.remove();
			return;
		}
		cleanNode(child);
		if (!KEEP_TAGS.has(tag)) {
			child.replaceWith(...Array.from(child.childNodes));
			return;
		}
		const allowed = ATTRS[tag] || [];
		Array.from(child.attributes).forEach((a) => {
			const name = a.name.toLowerCase();
			let ok = allowed.indexOf(name) !== -1; // no class, no style (server drops them)
			if (ok && (name === "href" || name === "src")) ok = safeUrl(a.value, name === "src");
			if (ok && name === "target") ok = a.value === "_blank";
			if (ok && name === "rel") ok = /^[a-z ]{1,64}$/.test(a.value);
			if (ok && name === "alt") ok = /^[^<>]{0,300}$/.test(a.value);
			if (!ok) child.removeAttribute(a.name);
		});
		if (tag === "A" && child.getAttribute("target") === "_blank") child.setAttribute("rel", "noopener noreferrer");
	});
}

export function sanitizeLpHtml(html) {
	if (!html) return "";
	if (typeof DOMParser === "undefined") return String(html).replace(/<[^>]*>/g, "");
	const doc = new DOMParser().parseFromString(`<!doctype html><body>${String(html)}</body>`, "text/html");
	cleanNode(doc.body);
	return doc.body.innerHTML;
}
